import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import { Brand } from '../types';

/* ---------- Types (mirror the backend JSON shape) ---------- */

export interface PartnershipPack {
  id: string;
  name: string;
  price: number;
  blurb?: string;
  highlight?: boolean;
  scarcity_note?: string;
  includes: string[];
  /** When true, the pack stays visible on the partner page but is no
   *  longer selectable — shown with a "No longer available" flag. */
  sold_out?: boolean;
  /** Optional cap — once `confirmed_count` reaches it, the public
   *  page treats the pack as sold-out (without flipping `sold_out`
   *  manually, so the editor still shows the real underlying state). */
  capacity?: number;
  /** Server-computed — number of `confirmed` responses on this pack.
   *  Only present in the public payload. */
  confirmed_count?: number;
}

export interface PartnershipFeature {
  id: string;
  label: string;
  hint?: string;
  /** Sub-items rendered indented under this feature on the public page.
   *  They travel with the parent — a pack includes the feature whole. */
  children?: PartnershipFeature[];
  /** Asset-type this feature produces when the "Generate draft assets"
   *  button runs. Empty / undefined = no asset is created (the feature
   *  is just a perk, e.g. priority support). Pick from the same
   *  ASSET_TYPES list used by the asset modal. */
  produces_asset_type?: string;
  /** How the asset is grouped across brands that include this feature.
   *  'shared' = one asset, every matching brand linked to it (multi-
   *  brand newsletter pattern). 'per_brand' = one asset per brand
   *  (dedicated newsletter / per-brand homepage banner / IG giveaway). */
  asset_grouping?: 'shared' | 'per_brand';
}

export interface PartnershipAddon {
  id: string;
  label: string;
  description?: string;
  price: number;
  /** Non-numeric pricing copy (free / on request). Shown instead of the
   *  € figure when price is 0 and this is set. */
  price_note?: string;
  /** Pack ids this add-on can be combined with. Empty/absent = all packs. */
  available_for?: string[];
  /** How many days within the campaign the add-on runs. We pick the
   *  actual dates based on demand — the page just shows the duration. */
  days?: number;
  /** Per-campaign extra (e.g. the 3DoD shoot) rather than a standard one. */
  campaign_specific?: boolean;
  /** Marks this add-on as the recommended one — light card + dark-green
   *  border + small "Recommended" badge on the partner page. */
  recommended?: boolean;
  scarcity_note?: string;
  /** Asset-type produced when the "Generate draft assets" button runs
   *  for a brand that bought this add-on. See PartnershipFeature for
   *  semantics. */
  produces_asset_type?: string;
  asset_grouping?: 'shared' | 'per_brand';
}

export interface PartnershipPreview {
  id: string;
  label: string;
  caption?: string;
  shape?: 'portrait' | 'landscape' | 'square';
  image?: string;
}

export interface PartnershipStat {
  value: string;
  label: string;
  /** Small clarifying line shown under the label on the partner page.
   *  Optional; blank = nothing rendered. */
  note?: string;
}

export interface PartnershipContact {
  name: string;
  email: string;
  role?: string;
}

export interface PartnershipCampaign {
  id: string;
  name: string;
  type: string;
  start_date: string;
  end_date: string;
  description?: string;
  brands: Brand[];
}

export interface PartnershipProsePreset {
  id: string;
  label: string;
  text: string;
}

export interface PartnershipCatalog {
  features: PartnershipFeature[];
  packs: PartnershipPack[];
  addons: PartnershipAddon[];
  intro_presets: PartnershipProsePreset[];
  responsibilities_presets: PartnershipProsePreset[];
}

export interface PartnershipFaqEntry {
  question: string;
  answer: string;
}

export interface Partnership {
  campaign: string;
  slug: string;
  eyebrow: string;
  intro: string;
  page_intro: string;
  stats: PartnershipStat[];
  /** Heading of the numbers strip; empty = only the "By the numbers" label. */
  stats_title: string;
  /** Short line next to the day picker on booking pages; empty = none. */
  days_note: string;
  features: PartnershipFeature[];
  packs: PartnershipPack[];
  addons: PartnershipAddon[];
  preview_images: Record<string, string>;
  preview_hidden: Record<string, boolean>;
  extra_previews: PartnershipPreview[];
  brand_responsibilities: string;
  extra_section_title: string;
  show_extra_section: boolean;
  confirmation_deadline: string | null;
  show_brands: boolean;
  /** Deal-tracking mode (Black Friday-style): shows the per-brand
   *  "order done" and "promo from" columns on the Outreach tab. */
  track_deals: boolean;
  /** Pages from before 2026-10-19 that really sold packs: only these still
   *  show packs, add-ons, previews, stats and auto-include / opt-out copy. */
  uses_packs: boolean;
  /** Minimum shopper discount to take part, as a decimal string; null = off. */
  min_discount_pct: string | null;
  /** nl / fr texts + "_source" / "_at" (see page_translate.py). */
  translations: Record<string, unknown>;
  translation_status?: { translated_at: string | null; languages: string[]; outdated: boolean };
  contacts: PartnershipContact[];
  /** When true, the public page renders a "closed" state and refuses
   *  new opt-in submissions. Toggled manually from the Logistics tab. */
  closed_for_opt_in: boolean;
  settlement_note: string;
  /** Small reassurance card shown above the packs ("you're in by default"). */
  auto_include_note: string;
  /** Line shown next to the opt-in CTA so brands can sit it out gracefully. */
  opt_out_note: string;
  faq: PartnershipFaqEntry[];
  /** Small note shown next to the "Confirm my participation" button on
   *  the public opt-in flow ("not final until confirmed", etc.). */
  participation_disclaimer: string;
  /** Templated body used by the Partners-table "Compose email" button.
   *  Placeholders: {first_name}, {brand}, {campaign}, {dates},
   *  {deadline_line}, {link}. */
  outreach_email_template: string;
  /** Body for the "Send confirmation" mailto: once Doreen locks a brand
   *  in. Placeholders: {first_name}, {brand}, {campaign}, {pack},
   *  {addons_line}, {discount}, {dates}. */
  confirmation_email_template: string;
  published: boolean;
  campaign_details: PartnershipCampaign;
  created_at: string;
  updated_at: string;
}

export type PublicPartnership = Omit<Partnership, 'campaign' | 'campaign_details' | 'published' | 'created_at' | 'updated_at'> & {
  campaign: PartnershipCampaign;
  /** Present only when the request carried a valid `?token=` — the
   *  brand-specific existing selection for the opt-in pre-fill. */
  response_preselection?: {
    selected_pack: string;
    selected_addons: string[];
    discount: string;
    brand_note: string;
    status: string;
  } | null;
  response_brand?: { id: string; name: string; country?: string } | null;
  /** Booking page data (pages without packs); null on pack pages. */
  booking: PublicBooking | null;
  /** nl / fr versions of the page's own texts (booking pages). */
  translations?: Partial<Record<'nl' | 'fr', PageTranslation>>;
};

/* ---------- Booking page (public) ---------- */

export interface PageTranslation {
  eyebrow?: string;
  intro?: string;
  page_intro?: string;
  extra_section_title?: string;
  brand_responsibilities?: string;
  participation_disclaimer?: string;
  faq?: { question: string; answer: string }[];
  stats_title?: string;
  days_note?: string;
  /** Same order as the English stats; blank parts fall back to the English. */
  stats?: PartnershipStat[];
}

export interface BookingSlot {
  id: number;
  start: string;
  end: string;
  spots: number;
  /** Spots still free (never who took the others). */
  left: number;
  price: number | string | null;
  /** Booked by the brand whose link this is. */
  mine: boolean;
  /** Marked as a stand-out slot by LampTwist. */
  highlighted: boolean;
  /** Expected numbers per day (meaning: the placement's stat_labels). */
  stats?: [number | null, number | null];
  /** Pack: the other placements that come with it that day. */
  with: string[];
}
export interface BookingPlacement {
  key: string;
  name: string;
  description: string;
  requirements: string;
  example_images: string[];
  category: string;
  pack_types: string[];
  /** Most slots one brand may book of this placement (null = no maximum). */
  max_per_brand?: number | null;
  /** What the slots' two numbers per day mean. */
  stat_labels?: [import('../stats').StatKey, import('../stats').StatKey];
  slots: BookingSlot[];
  /** nl / fr versions of name, description, requirements. */
  translations?: Partial<Record<'nl' | 'fr', { name?: string; description?: string; requirements?: string }>>;
}
export interface BookingFrame {
  id: number;
  one_slot?: boolean;
  rate_key: string | null;
  rate_label: string | null;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  views_per_week: number | null;
  clicks_per_week: number | null;
}
export interface BookingPage {
  key: string;
  title: string;
  sections: { id: number; label: string; image: string; width: number; height: number; frames: BookingFrame[] }[];
}
export interface PublicBooking {
  placements: BookingPlacement[];
  pages: BookingPage[];
  signed_up: boolean;
  my_slots: number[];
  /** Slots the team prepared for this brand, still free: ticked on load. */
  proposed_slots?: number[];
  /** Prepared slots another brand booked in the meantime. */
  proposed_gone?: { id: number; name: string; start: string }[];
}

/* ---------- Internal hooks (auth required) ---------- */

const partnershipKey = (campaignId: string) => ['partnership', campaignId] as const;
const contactsKey = ['partnerships', 'contacts'] as const;

export const usePartnership = (campaignId: string | undefined) =>
  useQuery({
    queryKey: partnershipKey(campaignId ?? ''),
    enabled: !!campaignId,
    retry: (count, err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
    queryFn: async (): Promise<Partnership> => {
      const res = await axios.get(`api/partnerships/${campaignId}/`);
      return res.data;
    },
  });

export const useCreatePartnership = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string): Promise<Partnership> => {
      const res = await axios.post('api/partnerships/', { campaign: campaignId });
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(partnershipKey(data.campaign), data);
    },
  });
};

export const useUpdatePartnership = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Partnership>): Promise<Partnership> => {
      const res = await axios.patch(`api/partnerships/${campaignId}/`, patch);
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(partnershipKey(campaignId), data);
    },
  });
};

export const useDeletePartnership = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string): Promise<void> => {
      await axios.delete(`api/partnerships/${campaignId}/`);
    },
    onSuccess: (_data, campaignId) => {
      qc.removeQueries({ queryKey: partnershipKey(campaignId) });
    },
  });
};

export const usePublishPartnership = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (publish: boolean): Promise<Partnership> => {
      const url = `api/partnerships/${campaignId}/${publish ? 'publish' : 'unpublish'}/`;
      const res = await axios.post(url);
      return res.data;
    },
    onSuccess: (data) => qc.setQueryData(partnershipKey(campaignId), data),
  });
};

export const useContactShortlist = () =>
  useQuery({
    queryKey: contactsKey,
    queryFn: async (): Promise<PartnershipContact[]> => {
      const res = await axios.get('api/partnerships-contacts/');
      return res.data;
    },
    staleTime: 5 * 60_000,
  });

export const usePartnershipCatalog = () =>
  useQuery({
    queryKey: ['partnerships', 'catalog'] as const,
    queryFn: async (): Promise<PartnershipCatalog> => {
      const res = await axios.get('api/partnerships-catalog/');
      return res.data;
    },
    staleTime: 30 * 60_000,
  });

/* ---------- Partner tracking (responses) ---------- */

export type PartnershipResponseStatus =
  | 'pending'
  | 'contacted'
  | 'sent-reminder'
  | 'follow-up'
  | 'replied'
  | 'confirmed'
  | 'auto-included'
  | 'declined'
  | 'excluded';

export type Settlement = '' | 'invoice' | 'credit-note' | 'extra-discount' | 'free';

export const SETTLEMENT_LABELS: Record<Settlement, string> = {
  '': 'Not set yet',
  invoice: 'Invoice',
  'credit-note': 'Credit note',
  'extra-discount': 'Extra discount',
  free: 'Free of charge',
};

export interface PartnershipResponse {
  id: number;
  partnership: string;
  brand: Brand;
  status: PartnershipResponseStatus;
  contacted_by: number | null;
  contacted_by_email: string | null;
  contacted_by_name: string | null;
  /** Mosy user owning the outreach for this brand. Also the account the
   *  email will be sent from once the auto-send flow exists. */
  assigned_to: number | null;
  assigned_to_email: string | null;
  assigned_to_name: string | null;
  contacted_at: string | null;
  replied_at: string | null;
  selected_pack: string;
  selected_addons: string[];
  /** Negotiated final price as a string ("1500.00"). Null = use the
   *  auto-computed default (selected pack + add-on prices). */
  final_price: string | null;
  /** Read-only — final_price if set, else the computed default. */
  effective_price: number;
  /** Free-text — "15%", "20% on lighting", "15% min", etc. */
  discount: string;
  /** True when the discount above was auto-prefilled from the brand's
   *  permanent discount and Doreen hasn't manually edited it since. */
  discount_auto: boolean;
  /** Booking pages: booked slot placements, and their names + dates. */
  booked_slots?: number[];
  booked?: { id: number; name: string; date: string | null; end_date: string | null; price: string | number | null; needs?: string }[];
  terms_accepted_at?: string | null;
  /** Booking pages: slots the team prepared for the brand (not reserved). */
  proposed_slots?: number[];
  /** Deal tracking (only shown when the partnership has track_deals on). */
  order_done: boolean;
  promo_from: string | null;
  promo_until: string | null;
  /** How the brand pays for its booking; '' = not decided yet. */
  settlement: Settlement;
  /** Free-text detail, e.g. "3% extra on PO P33785". */
  settlement_detail: string;
  note: string;
  /** Free-text remarks left by the brand at opt-in. Read-only for Doreen. */
  brand_note: string;
  /** Where this row's data came from. `manual` today; `opt_in_form` and
   *  `email` are reserved for the auto-flow coming next. */
  source: 'manual' | 'opt_in_form' | 'email';
  email_sent_at: string | null;
  opted_in_at: string | null;
  /** Unique token used to build the brand's personal opt-in link
   *  (?token=<x> on the partner page URL). Read-only. */
  opt_in_token: string;
  /** Odoo draft invoice created from this row, if any. All read-only —
   *  set by the "Generate invoice" button. `odoo_invoice_id` null = not
   *  invoiced yet. `odoo_invoice_number` stays "/" until the draft is
   *  posted in Odoo. */
  odoo_invoice_id: number | null;
  odoo_invoice_number: string;
  odoo_invoice_state: string;
  odoo_invoice_url: string;
  odoo_invoiced_at: string | null;
  created_at: string;
  updated_at: string;
}

const responsesKey = (campaignId: string) =>
  ['partnership-responses', campaignId] as const;

export const usePartnershipResponses = (campaignId: string | undefined) =>
  useQuery({
    queryKey: responsesKey(campaignId ?? ''),
    enabled: !!campaignId,
    queryFn: async (): Promise<PartnershipResponse[]> => {
      const res = await axios.get('api/partnership-responses/', {
        params: { partnership: campaignId },
      });
      return res.data;
    },
  });

export const useUpdatePartnershipResponse = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<Omit<PartnershipResponse, 'id' | 'brand' | 'partnership'>>;
    }): Promise<PartnershipResponse> => {
      const res = await axios.patch(`api/partnership-responses/${id}/`, patch);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: responsesKey(campaignId) });
    },
  });
};

export const useDeletePartnershipResponse = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`api/partnership-responses/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: responsesKey(campaignId) });
    },
  });
};

/* ---------- Odoo invoicing (billing tab) ---------- */

export interface OdooSupplier {
  id: number;
  name: string;
  vat: string;
  city: string;
  country_id: number | null;
  country_name: string;
}

/** Live search of Odoo vendors — enabled once the query is ≥ 2 chars. */
export const useOdooSupplierSearch = (query: string) =>
  useQuery({
    queryKey: ['odoo-suppliers', query.trim()] as const,
    enabled: query.trim().length >= 2,
    queryFn: async (): Promise<OdooSupplier[]> => {
      const res = await axios.get('api/odoo/suppliers/', {
        params: { q: query.trim() },
      });
      return res.data.results ?? [];
    },
  });

/** Bind an Odoo supplier to a brand (remembered for future invoices). */
export const useBindOdooSupplier = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      brandId,
      supplier,
    }: {
      brandId: string;
      supplier: OdooSupplier;
    }): Promise<Brand> => {
      const res = await axios.post(`api/brands/${brandId}/bind-odoo-supplier/`, {
        odoo_partner_id: supplier.id,
        odoo_partner_name: supplier.name,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: responsesKey(campaignId) });
      qc.invalidateQueries({ queryKey: ['brands'] });
    },
  });
};

export interface GenerateInvoiceResult {
  detail: 'created' | 'already-invoiced';
  invoice: { id: number; name: string; state: string; url: string };
  response: PartnershipResponse;
}

/** Create (or re-open) the draft invoice in Odoo for one billing row. */
export const useGenerateInvoice = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (responseId: number): Promise<GenerateInvoiceResult> => {
      const res = await axios.post(
        `api/partnership-responses/${responseId}/generate-invoice/`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: responsesKey(campaignId) });
    },
  });
};

export interface GeneratedPlacementsSummary {
  dry_run?: boolean;
  created: { title: string; asset_type: string; brands: string[] }[];
  updated: { asset_id: number; title: string; added_brands: string[] }[];
  skipped: { item: string; reason: string }[];
}

export const useGenerateDraftPlacements = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: { dryRun?: boolean; skipUpdateIds?: number[] } = {},
    ): Promise<GeneratedPlacementsSummary> => {
      const { dryRun = false, skipUpdateIds = [] } = args;
      const url = `api/partnerships/${campaignId}/generate-placements/${dryRun ? '?dry_run=true' : ''}`;
      const res = await axios.post(url, { skip_update_ids: skipUpdateIds });
      return res.data;
    },
    onSuccess: (data) => {
      // Only invalidate placements after a real apply.
      if (!data.dry_run) qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
};

export const useBrandPartnershipResponses = (brandId: string | undefined) =>
  useQuery({
    queryKey: ['partnership-responses', 'brand', brandId ?? ''] as const,
    enabled: !!brandId,
    queryFn: async (): Promise<PartnershipResponse[]> => {
      const res = await axios.get('api/partnership-responses/', {
        params: { brand: brandId },
      });
      return res.data;
    },
  });

/** Booking pages: put every campaign brand not on Outreach yet on it. */
export const useAddCampaignBrands = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ added: number }> =>
      (await axios.post('api/partnership-responses/add-campaign-brands/', { partnership: campaignId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: responsesKey(campaignId) });
      qc.invalidateQueries({ queryKey: partnershipKey(campaignId) });
    },
  });
};

/** Change a brand's booking from Outreach: add a free slot or remove one. */
export const useChangeBooking = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rowId, ...body }: { rowId: number; add?: number; remove?: number }) =>
      (await axios.post(`api/partnership-responses/${rowId}/booking/`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: responsesKey(campaignId) });
      qc.invalidateQueries({ queryKey: partnershipKey(campaignId) });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
};

export const useAddBrandToTrack = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (brandId: string): Promise<PartnershipResponse> => {
      const res = await axios.post('api/partnership-responses/add-brand/', {
        partnership: campaignId,
        brand_id: brandId,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: responsesKey(campaignId) });
      qc.invalidateQueries({ queryKey: partnershipKey(campaignId) });
    },
  });
};

/* ---------- Public hook (no auth) ---------- */

export interface OptInSubmitInput {
  slug: string;
  token: string;
  selected_pack?: string;
  selected_addons?: string[];
  /** Booking pages: the slot placements to book, and "I agree". */
  slots?: number[];
  agreed?: boolean;
  discount?: string;
  /** Free-text remarks from the brand (product wishes, giveaway ideas, etc.). */
  brand_note?: string;
}

export const submitPublicOptIn = async (input: OptInSubmitInput): Promise<{
  ok: true;
  brand: string | null;
  selected_pack: string;
  selected_addons: string[];
  discount: string;
  status: string;
}> => {
  const { slug, token, ...payload } = input;
  const res = await axios.post(
    `api/public/partnerships/${slug}/opt-in/${encodeURIComponent(token)}/`,
    payload,
  );
  return res.data;
};

export const fetchPublicPartnership = async (slug: string, token?: string): Promise<PublicPartnership> => {
  const url = token
    ? `api/public/partnerships/${slug}/?token=${encodeURIComponent(token)}`
    : `api/public/partnerships/${slug}/`;
  const res = await axios.get(url);
  return res.data;
};

/* ---------- Placements tab (booking, step 2) ---------- */

export interface PartnershipSlot {
  id: number;
  asset_type: string;
  title: string;
  publish_date: string | null;
  end_date: string | null;
  brand_spots: number;
  taken: number;
  brands: string[];
  /** The brands in booking order, with their sign-up time. */
  booking_order?: import('../types').BookingOrderEntry[];
  /** The placement's own price, when set (decimal string). */
  slot_price: string | null;
  /** Effective price of one slot: own price, else the type's price. */
  price: string | null;
  /** Stand-out slot on the booking page (subtle yellow edge). */
  highlighted: boolean;
  /** This day's own expected numbers per day (null = the campaign's). */
  slot_stat_1: number | null;
  slot_stat_2: number | null;
  /** Effective expected numbers per day (own, else campaign, else standard). */
  stats: [number | null, number | null];
  /** Pack only: the campaign's placements of the pack's other types that day. */
  pack_parts: { id: number; asset_type: string }[];
}

/** One sellable placement on the page (what the campaign's slot placements
 *  are "sold as"); its slots are those placements. */
export interface PartnershipPlacementRow {
  id: number;
  /** Placements & prices key, or "type:<asset_type>" when "Sold as" is empty. */
  key: string;
  asset_type: string;
  display_name: string;
  description: string;
  /** "What we need from you", shown to brands. */
  requirements: string;
  price: string | null;
  on_sale: boolean;
  /** The campaign's own mockup pictures (empty = follow the standard ones). */
  example_images: string[];
  /** Standard mockup pictures from Settings → Placements & prices. */
  standard_images?: string[];
  /** What brands see: own pictures, else the standard ones. */
  images?: string[];
  /** nl / fr versions of name, description, requirements. */
  translations?: Partial<Record<'nl' | 'fr', { name?: string; description?: string; requirements?: string }>>;
  sort_order: number;
  category: string;
  rate: { key: string; label: string; price: string; pack_types: string[] } | null;
  /** Standard price from Settings (null = none / on request). */
  standard_price: string | null;
  /** This campaign's maximum per brand (null = follow the standard). */
  max_per_brand: number | null;
  /** The line's standard maximum per brand (null = none). */
  standard_max: number | null;
  /** This campaign's expected numbers per day (null = the standard). */
  stat_1: number | null;
  stat_2: number | null;
  /** What the two numbers mean, e.g. ["page_views", "sessions"]. */
  stat_labels: [import('../stats').StatKey, import('../stats').StatKey];
  standard_stats: [number | null, number | null];
  campaign_stats: [number | null, number | null];
  /** Price per slot on this campaign: `price` if set, else the standard. */
  campaign_price: string | null;
  /** Slots whose "Sold as" is still empty (grouped by their type). */
  needs_sold_as: boolean;
  slots: PartnershipSlot[];
  spots_total: number;
  spots_taken: number;
}

const placementsKey = (campaignId: string) => ['partnership', campaignId, 'placements'] as const;

export const usePartnershipPlacements = (campaignId: string | undefined) =>
  useQuery({
    queryKey: placementsKey(campaignId ?? ''),
    enabled: !!campaignId,
    queryFn: async (): Promise<PartnershipPlacementRow[]> =>
      (await axios.get(`api/partnerships/${campaignId}/placements/`)).data,
  });

export const usePatchPartnershipPlacement = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: number } & Partial<Pick<PartnershipPlacementRow, 'display_name' | 'description' | 'requirements' | 'price' | 'max_per_brand' | 'stat_1' | 'stat_2' | 'on_sale' | 'example_images' | 'translations' | 'sort_order'>>) =>
      (await axios.patch(`api/partnership-placements/${id}/`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: placementsKey(campaignId) }),
  });
};
